import Script from "next/script";

type ClarityScriptProps = {
  /**
   * Microsoft Clarity project id (from `NEXT_PUBLIC_CLARITY_PROJECT_ID`).
   * When empty/unset the component no-ops, so nothing records until an app
   * supplies its own Clarity project id in Vercel.
   */
  projectId?: string;
};

/**
 * Clarity project ids are short lowercase-alphanumeric tokens (e.g.
 * "abcde12345"). Reject anything else so a placeholder or misconfigured
 * value never gets interpolated into the inline loader below.
 */
const CLARITY_ID_PATTERN = /^[a-z0-9]+$/i;

/**
 * Injects the official Microsoft Clarity loader once for the whole app.
 *
 * Mounted in {@link AppRoot} (the shared `<html>/<body>` shell) so it covers
 * every route — auth + dashboard — for the full member-zone funnel, not just
 * authenticated pages.
 *
 * Env-gated: no id → renders nothing. Uses the same inline `<Script>` pattern
 * as GTM (no extra dependency); the bootstrap async-loads
 * `https://www.clarity.ms/tag/<id>` after the page is interactive, so there
 * is no first-paint cost.
 */
export function ClarityScript({ projectId }: ClarityScriptProps) {
  if (!projectId) return null;

  if (!CLARITY_ID_PATTERN.test(projectId)) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `[ClarityScript] projectId "${projectId}" is not a valid Clarity id. Clarity not loaded.`,
      );
    }
    return null;
  }

  return (
    <Script id="ms-clarity" strategy="afterInteractive">
      {`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${projectId}");`}
    </Script>
  );
}
